import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  useToast,
  Flex,
  Text,
  IconButton,
  Spinner,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  Select,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import Swal from "sweetalert2";
import moment from "moment";
import {
  getControlGroupUsers,
  getControlGroupUserCount,
  uploadControlGroupUsers,
  deleteControlGroupUser,
  deleteMultipleControlGroupUsers,
  uploadSegmentUsersToExclusionList,
  getCustomSegments,
} from "../../Services/api";

const ControlGroupList = () => {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [total, setTotal] = useState(0);

  const [segments, setSegments] = useState([]);
  const [selectedSegment, setSelectedSegment] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const orgId = 1;
  const [uploadOption, setUploadOption] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchUserCount();
  }, [page]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await getControlGroupUsers(page - 1, perPage);
      setUsers(res?.data?.list || []);
    } catch (err) {
      console.error(err);
      toast({ title: "Failed to load users.", status: "error", isClosable: true });
    }
    setLoading(false);
  };

  const fetchUserCount = async () => {
    try {
      const res = await getControlGroupUserCount();
      setTotal(res?.data?.count || 0);
    } catch (err) {
      console.error(err);
      toast({ title: "Failed to fetch user count.", status: "error", isClosable: true });
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadControlGroupUsers(file);
      toast({ title: "Uploaded successfully.", status: "success", isClosable: true });
      fetchUsers();
      fetchUserCount();
    } catch (err) {
      console.error(err);
      toast({
        title: "Upload failed.",
        description: err?.response?.data?.message || err.message || "An unknown error occurred.",
        status: "error",
        isClosable: true,
      });
    }
    setUploading(false);
  };

  const handleDeleteSelected = async () => {
    if (!selectedRowKeys.length) {
      toast({ title: "Select users to delete.", status: "warning", isClosable: true });
      return;
    }
    Swal.fire({
      title: "Confirm Deletion",
      text: `Delete ${selectedRowKeys.length} users?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3182CE",
      cancelButtonColor: "#E53E3E",
      confirmButtonText: "Yes, delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteMultipleControlGroupUsers(selectedRowKeys);
          toast({ title: "Deleted successfully.", status: "success", isClosable: true });
          setSelectedRowKeys([]);
          fetchUsers();
          fetchUserCount();
        } catch (err) {
          console.error(err);
          toast({ title: "Deletion failed.", status: "error", isClosable: true });
        }
      }
    });
  };

  const handleDeleteSingle = async (customerId) => {
    Swal.fire({
      title: "Confirm Deletion",
      text: `Delete user ${customerId}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3182CE",
      cancelButtonColor: "#E53E3E",
      confirmButtonText: "Yes, delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteControlGroupUser(customerId);
          toast({ title: "User deleted.", status: "success", isClosable: true });
          fetchUsers();
          fetchUserCount();
        } catch (err) {
          console.error(err);
          toast({ title: "Deletion failed.", status: "error", isClosable: true });
        }
      }
    });
  };

  const fetchSegments = async () => {
    try {
      const res = await getCustomSegments(orgId);
      const enabledSegments = Array.isArray(res?.data?.data) ? res.data.data : [];
      setSegments(enabledSegments);
    } catch (err) {
      console.error(err);
      toast({ title: "Failed to fetch segments.", status: "error", isClosable: true });
    }
  };

  const handleSegmentUpload = () => {
    if (!selectedSegment) {
      toast({ title: "Please select a segment.", status: "warning", isClosable: true });
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "This will add users from the selected segment to the exclusion list.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3182CE",
      cancelButtonColor: "#E53E3E",
      confirmButtonText: "Yes, proceed",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await uploadSegmentUsersToExclusionList(selectedSegment);
          toast({ title: "Users uploaded successfully.", status: "success", isClosable: true });
          fetchUsers();
          fetchUserCount();
          setPopoverOpen(false);
          setUploadOption('');
        } catch (err) {
          console.error(err);
          toast({
            title: "Upload failed.",
            description: err?.response?.data?.message || err.message || "An unknown error occurred.",
            status: "error",
            isClosable: true,
          });
        }
      }
    });
  };

  const maskEmail = (email) => {
    if (!email) return "-";
    const [name, domain] = email.split("@");
    const maskedName = (name?.slice(0, 2) || "") + "*****";
    return `${maskedName}@${domain}`;
  };

  const maskPhone = (phone) => {
    if (!phone) return "-";
    return phone.slice(0, 2) + "*****" + phone.slice(-2);
  };

  const handleSelectRow = (customerId) => {
    if (selectedRowKeys.includes(customerId)) {
      setSelectedRowKeys(selectedRowKeys.filter((id) => id !== customerId));
    } else {
      setSelectedRowKeys([...selectedRowKeys, customerId]);
    }
  };

  return (
    <Box p={6}>
      <Text fontSize="2xl" fontWeight="bold" mb={6}>Control Group Users</Text>

      <Flex mb={6} justify="space-between" align="center">
        <Popover
          isOpen={popoverOpen}
          onClose={() => {
            setPopoverOpen(false);
            setSelectedSegment("");
            setUploadOption("");
            const input = document.getElementById("upload-csv");
            if (input) input.value = null;
          }}
          onOpen={() => {
            setPopoverOpen(true);
            fetchSegments();
          }}
        >
          <PopoverTrigger>
            <Button colorScheme="blue">Upload Users</Button>
          </PopoverTrigger>
          <PopoverContent p={4} maxW="sm">
            <PopoverArrow />
            <PopoverCloseButton />
            <PopoverHeader fontWeight="bold">Upload Method</PopoverHeader>
            <PopoverBody>
              <Flex direction="column" gap={3}>
                <Flex align="center">
                  <input type="radio" id="csv" name="uploadOption" value="csv" checked={uploadOption === "csv"} onChange={(e) => setUploadOption(e.target.value)} />
                  <label htmlFor="csv" style={{ marginLeft: 8 }}>Upload CSV</label>
                </Flex>
                {uploadOption === "csv" && (
                  <Input id="upload-csv" type="file" accept=".csv" onChange={handleUpload} isDisabled={uploading} />
                )}

                <Flex align="center">
                  <input type="radio" id="segment" name="uploadOption" value="segment" checked={uploadOption === "segment"} onChange={(e) => setUploadOption(e.target.value)} />
                  <label htmlFor="segment" style={{ marginLeft: 8 }}>Upload from Segment</label>
                </Flex>
                {uploadOption === "segment" && (
                  <>
                    <Select placeholder="Select a segment" onChange={(e) => setSelectedSegment(e.target.value)} value={selectedSegment}>
                      {segments.map((seg) => (
                        <option key={seg.id} value={seg.id}>{seg.name} ({seg.aggregates?.count_customers ?? 0} users)</option>
                      ))}
                    </Select>
                    <Button mt={2} size="sm" colorScheme="green" onClick={handleSegmentUpload} isDisabled={!selectedSegment}>
                      Upload Users
                    </Button>
                  </>
                )}
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Popover>

        <Button colorScheme="red" leftIcon={<DeleteIcon />} onClick={handleDeleteSelected} isDisabled={!selectedRowKeys.length}>
          Delete Selected
        </Button>
      </Flex>

      {loading ? (
        <Flex justify="center" align="center" height="300px">
          <Spinner size="xl" />
        </Flex>
      ) : (
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th><Checkbox isChecked={selectedRowKeys.length === users.length} isIndeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < users.length} onChange={(e) => setSelectedRowKeys(e.target.checked ? users.map((u) => u.customerId) : [])} /></Th>
              <Th>Customer ID</Th>
              <Th>Email</Th>
              <Th>Phone</Th>
              <Th>Uploaded Via</Th>
              <Th>Created Date</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {users.map((user) => (
              <Tr key={user.customerId}>
                <Td><Checkbox isChecked={selectedRowKeys.includes(user.customerId)} onChange={() => handleSelectRow(user.customerId)} /></Td>
                <Td>{user.customerId}</Td>
                <Td>{maskEmail(user.emailId)}</Td>
                <Td>{maskPhone(user.phoneNo)}</Td>
                <Td>{user.via ? user.via.charAt(0).toUpperCase() + user.via.slice(1) : '-'}</Td>
                <Td>{moment(user.createdDate).format("YYYY-MM-DD HH:mm")}</Td>
                <Td><IconButton aria-label="Delete" icon={<DeleteIcon />} colorScheme="red" size="sm" onClick={() => handleDeleteSingle(user.customerId)} /></Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <Flex justify="center" mt={6}>
        <Button variant="outline" onClick={() => setPage((prev) => Math.max(1, prev - 1))} isDisabled={page === 1} mr={2}>Previous</Button>
        <Button variant="outline" isDisabled>Page {page}</Button>
        <Button variant="outline" onClick={() => setPage((prev) => prev + 1)} isDisabled={page * perPage >= total} ml={2}>Next</Button>
      </Flex>
    </Box>
  );
};

export default ControlGroupList;
